import React, { useLayoutEffect, useRef, useState } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";

interface DropdownMenuProps {
  actionIsOpen: boolean;
  setActionIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onActionClick: (action: string) => void;
  // Pin state of the current selection: true = all selected are pinned (show
  // "Unpin"), false = show "Pin", null = nothing selected (default to "Pin").
  isPinned?: boolean | null;
}

const DROPDOWN_WIDTH = 176; // w-44
const EDGE_MARGIN = 16;

const ActionDropdown: React.FC<DropdownMenuProps> = ({
  actionIsOpen,
  setActionIsOpen,
  onActionClick,
  isPinned,
}) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  // Positioned "fixed" (computed from the trigger button's own position)
  // instead of "absolute" — the page's scroll container has
  // overflow-x-hidden, which clips an absolutely-positioned dropdown that
  // overflows it horizontally when the trigger sits mid-row on mobile.
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Handle click outside to close dropdown
  useClickOutside(dropdownRef, () => setActionIsOpen(false), true, "pointerdown");

  useLayoutEffect(() => {
    if (!actionIsOpen) return;
    const trigger = dropdownRef.current?.parentElement;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const idealLeft = rect.right - DROPDOWN_WIDTH;
    const maxLeft = window.innerWidth - DROPDOWN_WIDTH - EDGE_MARGIN;
    const left = Math.min(Math.max(idealLeft, EDGE_MARGIN), Math.max(maxLeft, EDGE_MARGIN));
    setPosition({
      top: rect.bottom + 8,
      left,
    });
  }, [actionIsOpen]);

  if (!actionIsOpen) return null;

  return (
    <div ref={dropdownRef}>
      <div
        id="dropdown-2"
        data-aspect="action-dropdown"
        style={
          position
            ? { top: position.top, left: position.left }
            : { visibility: "hidden" }
        }
        className="fixed z-50 bg-white divide-y divide-gray-100 rounded-lg shadow-2xl border border-gray-200 py-1 w-44 max-w-[calc(100vw-2rem)] origin-top-right"
      >
        <ul className="px-2 text-md text-gray-700">
          <li>
            <a
              href="#"
              className="flex items-center gap-2 py-2 px-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => onActionClick("pinFlight")}
            >
              <i className="st-thumbpin text-gray-600 text-md"></i>
              <p className="text-gray-800 text-md">
                {isPinned ? "Unpin Flight" : "Pin Flight"}
              </p>
            </a>
          </li>
          {/* <li>
            <a
              href="#"
              className="flex items-center gap-2 py-2"
              onClick={() => onActionClick("requestUpdate")}
            >
              <i className="st-reload text-lightgray-150 text-md"></i>
              <p className="text-lightgray-200 text-md cursor-pointer">
                Request Update
              </p>
            </a>
          </li>
          <li>
            <a
              href="#"
              className="flex items-center gap-2 py-2"
              onClick={() => onActionClick("enableStatusUpdate")}
            >
              <i className="st-enable text-lightgray-150 text-md"></i>
              <p className="text-lightgray-200 text-md">Enable Status Update</p>
            </a>
          </li> */}
          <li>
            <a
              href="#"
              className="flex items-center gap-2 py-2 px-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => onActionClick("deleteRequest")}
            >
              <i className="st-delete text-gray-600 text-md"></i>
              <p className="text-gray-800 text-md">Delete Request</p>
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ActionDropdown;
